import React from 'react';
import { openEditAppointment } from 'Components/Modals/Appointment/EditAppointment';
import { openEditOrder } from 'App/components/EditOrder';
import { FolderType, FileType, SortState } from '../AccountFiles';
import BemClass from '@upsales/components/Utils/bemClass';
import { getIconAndColor } from 'App/babel/helpers/file';
import T from 'Components/Helpers/translate';
import {
	EllipsisTooltip,
	Icon,
	Link,
	Table,
	TableColumn,
	TableHeader,
	TableRow,
	Text,
	Tooltip
} from '@upsales/components';
import FolderRow from './FolderRow';
import { goToContactCard } from 'App/helpers/contactCardNavigation';
import moment from 'moment';
import './ListView.scss';

type Props = {
	folders: FolderType[];
	files: FileType[];
	openFolder: (folderId: number, name: string) => void;
	deleteFolder: (id: number) => void;
	deleteFile: (file: FileType) => void;
	changeNameOfFolder: (id: number, name: string) => void;
	sort: SortState;
	onSortChange: (sort: SortState) => void;
	folderPaginator?: React.ReactNode;
};

const ListView = ({
	folders,
	files,
	openFolder,
	deleteFolder,
	deleteFile,
	changeNameOfFolder,
	sort,
	onSortChange,
	folderPaginator
}: Props) => {
	const classes = new BemClass('FilesListView');

	const openRelated = (e: React.FormEvent, entity: string, entityId: number) => {
		e.stopPropagation();

		switch (entity) {
			case 'Contact':
				goToContactCard(entityId);
				break;

			case 'Order':
			case 'Opportunity':
				openEditOrder({ customerId: Tools.AppService.getCustomerId(), id: entityId });
				break;

			case 'Activity':
				Tools.$upModal.open('editActivity', { id: entityId });
				break;

			case 'Appointment':
				openEditAppointment({ id: entityId });
				break;
		}
	};

	const getRow = (file: FileType) => {
		const [iconName, color] = getIconAndColor(file?.extension);
		return (
			<TableRow
				className={classes.elem('file').b()}
				key={file.id}
				onClick={(e: React.MouseEvent<HTMLElement>) => {
					e.stopPropagation();
					Tools.File.download(file.id);
				}}
			>
				<TableColumn>
					<EllipsisTooltip title={file.filename.match(/.{1,20}/g)?.join('\n')}>
						<Text className={classes.elem('file').elem('name').b()}>
							<Icon className={classes.elem('folder-icon').b()} color={color} name={iconName} />
							{file.filename}
						</Text>
					</EllipsisTooltip>
				</TableColumn>
				<TableColumn>
					<Text>{`${Math.round(file.size / 10) / 100} Kb`}</Text>
				</TableColumn>
				<TableColumn>
					<Text>{moment(file.uploadDate).format('L')}</Text>
				</TableColumn>
				<TableColumn>
					<Text>{Tools.AppService.getUsers().find(u => u.id === file.user?.id)?.name ?? ''}</Text>
				</TableColumn>
				<TableColumn className={classes.elem('last-column').b()}>
					{file.entity && file.entity !== 'Client' ? (
						<Link
							onClick={e => {
								if (file.entity && file.entityId) {
									openRelated(e, file.entity, file.entityId);
								}
							}}
						>
							{file.entity}
						</Link>
					) : (
						<div></div>
					)}
					<Tooltip className={classes.elem('delete-button').b()} title="Delete" position="left">
						{file.userRemovable ? (
							<Icon
								onClick={e => {
									e.stopPropagation();
									deleteFile(file);
								}}
								color="grey-6"
								name="trash-o"
							/>
						) : null}
					</Tooltip>
				</TableColumn>
			</TableRow>
		);
	};

	const folderColumns = [{ title: T('default.name'), sort: 'name' }, { title: '' }];

	const fileColumns = [
		{ title: T('default.name'), sort: 'filename' },
		{ title: T('file.fileSize'), sort: 'size', sortType: 'numeric' },
		{ title: T('file.uploadedDate'), sort: 'uploadDate', sortType: 'numeric' },
		{ title: T('file.uploader'), sort: 'user.id' },
		{ title: T('file.relatedTo'), sort: 'entity' }
	];

	return (
		<div className={classes.b()}>
			{folders.length ? (
				<Table className={classes.elem('folderList').b()}>
					<TableHeader color="grey" columns={folderColumns} />
					{folders.map((folder: FolderType) => (
						<FolderRow
							key={folder.id}
							folder={folder}
							openFolder={openFolder}
							deleteFolder={deleteFolder}
							changeNameOfFolder={changeNameOfFolder}
						/>
					))}
				</Table>
			) : null}
			{folderPaginator}
			{files.length ? (
				<Table>
					<TableHeader
						color="grey"
						columns={fileColumns}
						sorting={sort.field}
						asc={sort.asc}
						onSortChange={s => {
							onSortChange({ field: s.field, asc: s.asc });
						}}
					/>
					{files.map((file: FileType) => getRow(file))}
				</Table>
			) : null}
		</div>
	);
};

export default ListView;
